import { cva } from "class-variance-authority";

export type PaginationVariant =
  | "search"
  | "list"
  | "table"
  | "arbitrator"
  | "compact"
  | "sheet"
  | "prompt"
  | "upload";

export const stepIconVariants = cva(
  "inline-flex items-center justify-center cursor-pointer transition-colors disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  {
    variants: {
      variant: {
        search:
          "size-7 rounded-md enabled:hover:bg-contrast/4 disabled:opacity-40 disabled:cursor-not-allowed",
        list: "size-7 rounded-md text-contrast/70 enabled:hover:text-contrast/90 disabled:opacity-40 disabled:cursor-not-allowed",
        table:
          "flex size-8 items-center justify-center rounded-md bg-contrast/8 text-contrast/60 transition-colors enabled:hover:bg-contrast/[0.12] disabled:opacity-40 cursor-pointer disabled:cursor-default focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
        arbitrator: "",
        compact: "",
        sheet: "",
        prompt: "",
        upload:
          "size-7 rounded flex items-center justify-center text-contrast/30 enabled:hover:bg-contrast/4 disabled:opacity-30 disabled:cursor-not-allowed",
      },
    },
    defaultVariants: { variant: "search" },
  },
);

/** Prev / Next with optional label (bordered pill) */
export const stepLabeledVariants = cva(
  "inline-flex items-center cursor-pointer transition-colors disabled:cursor-not-allowed focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary",
  {
    variants: {
      variant: {
        search:
          "gap-1.5 h-7 px-1.5 rounded-md border border-contrast/5 enabled:hover:bg-contrast/4 disabled:opacity-40",
        list: "gap-2 h-7 px-1.5 rounded-md border border-contrast/5 text-contrast/70 enabled:hover:bg-contrast/4 disabled:opacity-40 disabled:cursor-not-allowed",
        table: "",
        arbitrator:
          "h-7 items-center gap-2 rounded-md border border-border px-1.5 text-xs transition-colors enabled:hover:bg-contrast/6 disabled:opacity-40 cursor-pointer disabled:cursor-default",
        compact: "",
        sheet:
          "gap-1 h-7 px-1.5 rounded-md border border-contrast/8 bg-contrast/[0.02] text-[13px] text-contrast/75 enabled:hover:bg-contrast/4 disabled:opacity-40",
        prompt:
          "gap-1.5 h-7 px-2 rounded-md border border-contrast/8 bg-contrast/[0.02] text-[13px] text-contrast/75 enabled:hover:bg-contrast/4 disabled:opacity-40",
        upload: "",
      },
    },
    defaultVariants: { variant: "search" },
  },
);

/**
 * Prev/Next label text, paired with stepLabeledVariants. `direction` carries
 * the prev-vs-next emphasis (next reads a touch stronger than prev).
 */
export const stepLabelVariants = cva("", {
  variants: {
    variant: {
      search: "text-xs font-normal",
      list: "text-xs",
      table: "",
      arbitrator: "",
      compact: "",
      sheet: "text-[13px] text-contrast/75",
      prompt: "text-[13px] text-contrast/75",
      upload: "",
    },
    direction: { prev: "", next: "" },
  },
  compoundVariants: [
    { variant: "search", direction: "prev", class: "text-contrast/70" },
    { variant: "search", direction: "next", class: "text-contrast/85" },
    { variant: "list", direction: "prev", class: "text-contrast/70" },
    { variant: "list", direction: "next", class: "text-contrast/85" },
    { variant: "arbitrator", direction: "prev", class: "text-contrast/70" },
    { variant: "arbitrator", direction: "next", class: "text-contrast/85" },
  ],
  defaultVariants: { variant: "search", direction: "prev" },
});

/** Prev/Next chevron colour, paired with stepLabeledVariants. */
export const stepLabelIconVariants = cva("", {
  variants: {
    variant: {
      search: "text-heading",
      list: "text-heading",
      table: "text-heading",
      arbitrator: "",
      compact: "text-heading",
      sheet: "text-heading",
      prompt: "text-heading",
      upload: "text-heading",
    },
    direction: { prev: "", next: "" },
  },
  compoundVariants: [
    { variant: "arbitrator", direction: "prev", class: "text-contrast/70" },
    { variant: "arbitrator", direction: "next", class: "text-contrast/85" },
  ],
  defaultVariants: { variant: "search", direction: "prev" },
});

export const pageNumberVariants = cva(
  "inline-flex items-center justify-center font-medium cursor-pointer transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        search:
          "size-7 rounded-md text-sm bg-none text-heading border border-border",
        list: "size-7 rounded-md text-sm text-heading border border-contrast/[0.15]",
        table: "flex size-8 items-center justify-center rounded-md text-sm",
        arbitrator:
          "flex size-7 items-center justify-center rounded-md text-sm text-heading border border-contrast/15 bg-contrast/[0.02]",
        compact:
          "size-5 rounded-sm text-contrast/70 border border-border/60 enabled:hover:bg-contrast/4 disabled:opacity-40",
        sheet:
          "size-7 rounded-md border text-[13px] border-contrast/8 bg-contrast/[0.02] text-contrast/70 enabled:hover:bg-contrast/4 disabled:opacity-40",
        prompt:
          "size-7 rounded-md border text-[13px] border-contrast/8 bg-contrast/[0.02] text-contrast/70 enabled:hover:bg-contrast/4 disabled:opacity-40",
        upload:
          "size-7 rounded flex items-center justify-center text-sm cursor-pointer",
      },
      active: {
        true: "",
        false: "",
      },
    },
    compoundVariants: [
      {
        variant: "search",
        active: true,
        class: "bg-contrast/6",
      },
      {
        variant: "search",
        active: false,
        class: "bg-contrast/[0.02] enabled:hover:bg-contrast/4",
      },
      {
        variant: "list",
        active: true,
        class: "bg-contrast/6",
      },
      {
        variant: "list",
        active: false,
        class: "bg-contrast/[0.02] enabled:hover:bg-contrast/6",
      },
      {
        variant: "table",
        active: true,
        class: "border border-contrast/[0.15] bg-contrast/5 text-heading",
      },
      {
        variant: "table",
        active: false,
        class: "text-contrast/50 enabled:hover:bg-contrast/6",
      },
      {
        variant: "arbitrator",
        active: false,
        class: "enabled:hover:bg-contrast/6",
      },
      {
        variant: "sheet",
        active: true,
        class: "border-contrast/[0.15] bg-contrast/6 text-heading",
      },
      {
        variant: "prompt",
        active: true,
        class: "border-contrast/[0.15] bg-contrast/6 text-heading",
      },
      {
        variant: "upload",
        active: true,
        class: "font-medium text-heading bg-contrast/6 border border-border",
      },
      {
        variant: "upload",
        active: false,
        class: "text-contrast/40 enabled:hover:bg-contrast/4",
      },
    ],
    defaultVariants: { variant: "search", active: false },
  },
);

export const navRowVariants = cva("flex items-center", {
  variants: {
    variant: {
      search: "justify-center md:justify-start gap-2 md:gap-3",
      list: "gap-3",
      table: "gap-1",
      arbitrator: "gap-3",
      compact: "gap-1",
      sheet: "gap-1.5 shrink-0",
      prompt: "gap-2",
      upload: "gap-1 justify-center",
    },
  },
  defaultVariants: { variant: "search" },
});
